import React, { useEffect, useState } from "react";
import { Field, Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useParams, useNavigate } from "react-router-dom";
import { useMask } from "@react-input/mask";
import loginStyles from "pages/Login/Login.module.scss";
import { LoginLayout } from "pages/Login/Login";
import Button, { ButtonWrapper } from "components/Button/Button";
import { FormControl, FormFeedback, FormLabel, FormRow, FormStep } from "components/Form/Form";
import { validateCPF } from "utils/validateCPF";
import { api } from "services";
import styles from "./FindEmail.module.scss";

const cpfSchema = Yup.object().shape({
  cpf: Yup.string()
    .required("Campo obrigatório")
    .test("cpf", "CPF Inválido", (value) => validateCPF(value)),
});

export default function FindEmail() {
  const navigate = useNavigate();
  const { step } = useParams();
  const [email, setEmail] = useState("");
  const [otherEmails, setOtherEmails] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!step) {
      navigate("/consultar-email/informar-cpf");
    }
  }, [step, navigate]);

  const cpfRef = useMask({
    mask: "___.___.___-__",
    replacement: { _: /\d/ },
  });

  const handleSubmit = async (values, actions) => {
    const formatedCpf = values.cpf.replace(/\D/g, "");
    const loaderId = toast.loading("Consultando CPF...");
    setLoading(true);

    try {
      const { data } = await api.get(`/users/${formatedCpf}`);

      const principalEmail = data.existing_user?.email || data?.seduc?.email_principal || data?.seduc?.institutional_email;

      const normalizedPrincipal = (principalEmail || "").toLowerCase();
      const seen = new Set();
      const vinculos = (data?.seduc?.vinculos || []).filter((vinculo) => {
        if (!vinculo.email) return false;
        const normalizedEmail = vinculo.email.toLowerCase();
        if (normalizedEmail === normalizedPrincipal || seen.has(normalizedEmail)) {
          return false;
        }
        seen.add(normalizedEmail);
        return true;
      });

      setEmail(principalEmail);
      setOtherEmails(vinculos);
      navigate("/consultar-email/resultado");
    } catch (error) {
      if (error.response?.status === 404) {
        actions.setFieldError("cpf", "CPF não encontrado no iSeduc. Entre em contato com sua escola para verificar seu cadastro.");
      } else {
        actions.setFieldError("cpf", error.response?.data?.error || "Erro ao consultar CPF.");
      }
    } finally {
      toast.dismiss(loaderId);
      setLoading(false);
    }
  };

  const handleCopyEmail = async (emailToCopy) => {
    await navigator.clipboard.writeText(emailToCopy);
    toast.success("E-mail copiado!");
  };

  const handleBackButton = () => {
    if (step === "resultado") return "/consultar-email/informar-cpf";
    return "/login";
  };

  return (
    <LoginLayout back={handleBackButton()} contentWidth={550} contentMinWidth={otherEmails.length > 0 ? 630 : undefined}>
      <FormStep show={step === "informar-cpf"}>
        <Formik initialValues={{ cpf: "" }} validationSchema={cpfSchema} onSubmit={handleSubmit}>
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <div>
                  <h1 className={loginStyles.title}>
                    Vamos consultar seus dados na <strong>Rede SEDUC.</strong>
                  </h1>
                  <p className={loginStyles.description}>Digite seu CPF para localizarmos seu e-mail de acesso</p>
                </div>

                <FormRow>
                  <FormLabel htmlFor="cpf">Digite o seu CPF</FormLabel>
                  <FormControl id="cpf" name="cpf" type="text" value={props.values.cpf} invalid={props.errors.cpf && props.touched.cpf} placeholder="000.000.000-00" ref={cpfRef} />
                  <ErrorMessage name="cpf">{(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}</ErrorMessage>
                </FormRow>
              </div>

              <ButtonWrapper>
                <Button modifiers={["primary"]} type="submit" style={{ minWidth: 134 }} disabled={props.isSubmitting || !props.values.cpf || loading}>
                  Consultar
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={step === "resultado"}>
        {email ? (
          <>
            <div className={loginStyles.body}>
              {otherEmails.length > 0 ? (
                <>
                  <div className={loginStyles.textGroup}>
                    <h1 className={loginStyles.title} style={{ fontSize: "1.8rem" }}>
                      Encontramos os seguintes e-mails SEDUC.PI vinculados ao seu CPF:
                    </h1>
                    <p className={loginStyles.description}>Escolha um dos emails abaixo para acessar a plataforma:</p>
                  </div>

                  <FormRow>
                    <ul className={styles["email-list"]}>
                      <li className={styles["email-item"]}>
                        <span className={styles["email-item-text"]}>
                          {email}
                          <span className={`${styles.badge} ${styles["badge-primary"]}`}>Principal</span>
                        </span>
                        <Button modifiers={["outline"]} onClick={() => handleCopyEmail(email)} style={{ padding: "8px 16px", fontSize: "1.4rem" }}>
                          Copiar e-mail
                        </Button>
                      </li>
                      {otherEmails.map((vinculo) => (
                        <li key={vinculo.email} className={styles["email-item"]}>
                          <span className={styles["email-item-text"]}>{vinculo.email}</span>
                          <Button modifiers={["outline"]} onClick={() => handleCopyEmail(vinculo.email)} style={{ padding: "8px 16px", fontSize: "1.4rem" }}>
                            Copiar e-mail
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </FormRow>

                  <div className={styles.instructions}>
                    <p className={styles["instructions-title"]}>Após a escolha:</p>
                    <ol className={styles["instructions-list"]}>
                      <li>Copie o e-mail de sua preferência para acessar a plataforma.</li>
                      <li>Clique em "Continuar".</li>
                      <li>Na tela de login, clique em "Entrar com e-mail SEDUC.PI".</li>
                      <li>Cole o e-mail copiado e finalize o login com sua conta Google.</li>
                    </ol>
                  </div>

                  <p className={styles["principal-note"]}>O e-mail que você usar agora se tornará o seu e-mail principal. Use-o nos próximos acessos.</p>
                </>
              ) : (
                <>
                  <div className={loginStyles.textGroup}>
                    <h1 className={loginStyles.title} style={{ fontSize: "1.8rem" }}>
                      <strong>E-mail localizado com sucesso!</strong>
                      <br />
                      Este é o seu novo acesso oficial para acessar a plataforma:
                    </h1>
                  </div>

                  <FormRow>
                    <div className={styles["email-result"]}>
                      <span>{email}</span>
                      <Button
                        modifiers={["outline"]}
                        onClick={() => handleCopyEmail(email)}
                        style={{
                          padding: "8px 16px",
                          fontSize: "1.4rem",
                        }}
                      >
                        Copiar e-mail
                      </Button>
                    </div>
                  </FormRow>

                  <p className={loginStyles.body} style={{ fontSize: "1.8rem" }}>
                    Copie o e-mail acima. Você vai precisar colocá-lo na próxima tela para entrar na plataforma.
                  </p>
                </>
              )}
            </div>
            <ButtonWrapper style={otherEmails.length > 0 ? { marginTop: "-20px" } : undefined}>
              <Button modifiers={["outline"]} onClick={() => navigate("/login", { state: { skipAccessInfoModal: true } })} style={{ minWidth: 134 }}>
                Continuar
              </Button>
            </ButtonWrapper>
          </>
        ) : (
          <div className={loginStyles.body}>
            <div className={loginStyles.textGroup} style={{ fontSize: "1.8rem", gap: 0 }}>
              <p style={{ marginBottom: "2rem", fontWeight: "bold" }}>Desculpe, mas não conseguimos encontrar seu e-mail.</p>
              <h1 className={loginStyles.title}>Vá até a sua escola para corrigir seus dados. Depois disso, é só voltar aqui e finalizar seu cadastro para ter acesso aos conteúdos e começar a estudar!</h1>
            </div>
          </div>
        )}
      </FormStep>
    </LoginLayout>
  );
}
